// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useTaskQuery } from "@/api/hooks/use-tasks";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { KeyChip } from "@/components/shared/key-chip";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { RequirePermission } from "@/components/shared/require-permission";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { UnsavedChangesDialog } from "@/components/shared/unsaved-changes-dialog";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TaskActionsMenu } from "@/features/tasks/task-actions-menu";
import { TaskAvailabilityTab } from "@/features/tasks/task-availability-tab";
import { TaskEditTab, type TaskEditTabHandle } from "@/features/tasks/task-edit-tab";
import { TaskInfoTab } from "@/features/tasks/task-info-tab";
import { TaskKindBadge } from "@/features/tasks/task-kind-badge";
import { ToggleTaskActiveDialog } from "@/features/tasks/toggle-task-active-dialog";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

export const Route = createFileRoute("/_authenticated/tasks/$subtype")({
  validateSearch: z.object({ t: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.tasks}>
      <TaskDetailPage />
    </RequirePermission>
  ),
});

function TaskDetailPage() {
  const { subtype } = Route.useParams();
  const { t: tab } = Route.useSearch();
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.tasksEdit);
  const canSeeAvailability = hasPermission(PAGE_PERMISSIONS.tasksAvailability);
  const { data: task, isLoading, isError } = useTaskQuery(subtype);

  // A built-in type has no definition to configure, so the Edit tab is out. Its availability does
  // exist though — one `action_task_settings` row per store — so that tab stays.
  const isBuiltIn = task?.kind === "builtIn";
  const requestedTab = tab ?? "info";
  const tabAllowed: Record<string, boolean> = {
    info: true,
    availability: canSeeAvailability,
    edit: canEdit && !isBuiltIn,
  };
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";

  const [isEditDirty, setIsEditDirty] = useState(false);
  const [toggleOpen, setToggleOpen] = useState(false);
  const editTabRef = useRef<TaskEditTabHandle>(null);
  const blocker = useBlocker({
    shouldBlockFn: () => isEditDirty,
    enableBeforeUnload: () => isEditDirty,
    withResolver: true,
  });

  const breadcrumbs = useMemo(() => [{ label: t("tasks.title"), to: "/tasks" }], [t]);

  function handleTabChange(newTab: string) {
    navigate({
      to: "/tasks/$subtype",
      params: { subtype },
      search: newTab === "info" ? {} : { t: newTab },
      replace: true,
    });
  }

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError || !task) {
    return (
      <div>
        <PageHeader title={subtype} breadcrumbs={breadcrumbs} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={task.fullName}
        subtitle={
          <>
            {task.customTaskDefinitionId != null && <IdPill id={task.customTaskDefinitionId} />}
            <KeyChip label={task.key} />
            <TaskKindBadge kind={task.kind} />
            <StatusIndicator active={task.isActive} />
            {!isBuiltIn && (
              <TaskActionsMenu
                isActive={task.isActive}
                onToggleActive={() => setToggleOpen(true)}
                triggerClassName="ml-auto -my-1.5"
              />
            )}
          </>
        }
        breadcrumbs={breadcrumbs}
      />
      <div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
          <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
            <TabsList>
              <TabsIndicator />
              <TabsTrigger value="info">{t("tasks.detail.tabs.info")}</TabsTrigger>
              {tabAllowed.availability && (
                <TabsTrigger value="availability">{t("tasks.detail.tabs.availability")}</TabsTrigger>
              )}
              {tabAllowed.edit && <TabsTrigger value="edit">{t("tasks.detail.tabs.edit")}</TabsTrigger>}
            </TabsList>
          </div>
          <TabsContent value="info" className="space-y-4">
            <TaskInfoTab task={task} />
          </TabsContent>
          {tabAllowed.availability && (
            <TabsContent value="availability" className="space-y-4">
              <TaskAvailabilityTab subtype={subtype} kind={task.kind} active={activeTab === "availability"} />
            </TabsContent>
          )}
          {tabAllowed.edit && (
            <TabsContent value="edit" className="space-y-4">
              <TaskEditTab ref={editTabRef} subtype={subtype} canEdit={canEdit} onDirtyChange={setIsEditDirty} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <ToggleTaskActiveDialog
        task={
          toggleOpen
            ? {
                subtype: task.key,
                name: task.fullName,
                isActive: task.isActive,
              }
            : null
        }
        onClose={() => setToggleOpen(false)}
      />

      <UnsavedChangesDialog
        open={blocker.status === "blocked"}
        onStay={() => blocker.reset?.()}
        onDiscard={() => {
          editTabRef.current?.reset();
          blocker.proceed?.();
        }}
      />
    </div>
  );
}
